import axios, {AxiosResponse} from 'axios';
import {Status} from '../models/enums';
import {routes} from '../routes/routes';
import axiosInstance from '../utils/AxiosInstans';
import {PreReserveQuery, ReservationQuery} from './models/requestQueries';
import {
  AuthResponseSignUpDto,
  ReservationExpiresTimeRes,
  ReservationOrganizationUnitResponse,
  ReservationPatternsResponse,
  ReservationTagsResponse,
  ResPreReserveDTO,
  ResponseReserveViewResponseDto,
} from './models/response/ReservationResService';
import {
  CancelReservationDto,
  CreateReserveDto,
  PreReserveDTO,
} from './models/request/ReservationReqService';
import {handleMutationError} from '../utils/helpers/errorHandler';
import {toOptionalOrganizationUnitId} from '../utils/helpers/organizationUnits';

const {
  baseUrl,
  reservation: {
    getTags,
    getPatterns,
    getOrganizationUnit,
    getReservation,
    preReserve,
    calculatePrice,
    submit,
    cancel,
    getExpiresTime,
  },
} = routes;

const ReservationService = {
  GetOrganizationUnit:
    async (): Promise<ReservationOrganizationUnitResponse> => {
      try {
        const response =
          await axiosInstance.get<ReservationOrganizationUnitResponse>(
            baseUrl + getOrganizationUnit(),
          );
        if (response.status === Status.Ok) {
          return response.data;
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        console.error(
          'Error in GetReservationOrganizationUnit function:',
          error,
        );
        if (axios.isAxiosError(error) && error.response) {
          handleMutationError(error);
          throw new Error(
            error.response?.data?.message || 'Unknown error occurred',
          );
        }
        throw error;
      }
    },

  GetTags: async (
    organizationUnitId?: number,
  ): Promise<ReservationTagsResponse> => {
    try {
      const unitId = toOptionalOrganizationUnitId(organizationUnitId);
      const response = await axiosInstance.get<ReservationTagsResponse>(
        baseUrl + getTags(unitId ? {organizationUnitId: unitId} : undefined),
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetTags function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  GetPatterns: async (
    organizationUnitId?: number,
  ): Promise<ReservationPatternsResponse> => {
    try {
      const unitId = toOptionalOrganizationUnitId(organizationUnitId);
      const response = await axiosInstance.get<ReservationPatternsResponse>(
        baseUrl +
          getPatterns(unitId ? {organizationUnitId: unitId} : undefined),
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetPatterns function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  GetReservation: async (
    query: ReservationQuery,
    signal?: AbortSignal,
  ): Promise<ResponseReserveViewResponseDto> => {
    try {
      const response = await axiosInstance.get<ResponseReserveViewResponseDto>(
        baseUrl + getReservation(query),
        {signal},
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetReservation function:', error);
      // Don't handle AbortError - let React Query handle it
      if (axios.isCancel(error)) {
        throw error;
      }
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  PreReserve: async (
    query: PreReserveQuery,
  ): Promise<AuthResponseSignUpDto> => {
    try {
      const response = await axiosInstance.post<
        PreReserveQuery,
        AxiosResponse<AuthResponseSignUpDto>
      >(baseUrl + preReserve(query), {});
      if (response.status === Status.Ok || response.status === Status.Created) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in PreReserve function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  CalculatePrice: async (body: PreReserveDTO): Promise<ResPreReserveDTO> => {
    try {
      const response = await axiosInstance.post<
        PreReserveDTO,
        AxiosResponse<ResPreReserveDTO>
      >(baseUrl + calculatePrice(), body);
      if (response.status === Status.Ok || response.status === Status.Created) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in CalculatePrice function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  SubmitReservation: async (
    body: CreateReserveDto,
  ): Promise<AuthResponseSignUpDto> => {
    try {
      const response = await axiosInstance.post<
        CreateReserveDto,
        AxiosResponse<AuthResponseSignUpDto>
      >(baseUrl + submit(), body);
      if (response.status === Status.Ok || response.status === Status.Created) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in SubmitReservation function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  CancelReservation: async (
    body: CancelReservationDto,
  ): Promise<AuthResponseSignUpDto> => {
    try {
      const response = await axiosInstance.post<
        CancelReservationDto,
        AxiosResponse<AuthResponseSignUpDto>
      >(baseUrl + cancel(), body);
      if (response.status === Status.Ok || response.status === Status.Created) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in CancelReservation function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  GetExpiresTime: async (): Promise<ReservationExpiresTimeRes> => {
    try {
      const response = await axiosInstance.get<ReservationExpiresTimeRes>(
        baseUrl + getExpiresTime(),
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetExpiresTime function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },
};

export default ReservationService;
